<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import BoardsAPI from 'kanban/api/boards';
import TaskAttributeFiltersPanel from 'kanban/components/filters/TaskAttributeFiltersPanel.vue';
import { isValidCondition } from 'kanban/helper/taskFilters';

// Bloco "tem cartao no funil" do segmento de contatos (spec 007): funil, etapas, situacao e condicoes de atributo do
// cartao pelo editor do funil (spec 005). O modelo e o `query.kanban` do segmento; nulo = sem bloco.
const block = defineModel({ type: Object, default: null });

const STATUSES = ['open', 'completed', 'cancelled'];
const STATUS_LABELS = {
  open: 'CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.STATUS_OPTIONS.OPEN',
  completed: 'CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.STATUS_OPTIONS.COMPLETED',
  cancelled: 'CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.STATUS_OPTIONS.CANCELLED',
};

const { t } = useI18n();
const getAttributesByModel = useMapGetter('attributes/getAttributesByModel');

const boards = ref([]);
const showAttributes = ref(false);

const definitions = computed(() =>
  getAttributesByModel.value('task_attribute')
);
const selectedBoard = computed(() =>
  boards.value.find(board => board.id === block.value?.board_id)
);
const steps = computed(() => selectedBoard.value?.steps_summary || []);
const attributeFilters = computed(() => block.value?.attribute_filters || []);

const update = changes => {
  block.value = { ...block.value, ...changes };
};

const addBlock = () => {
  block.value = {
    board_id: null,
    board_step_ids: [],
    status: null,
    attribute_filters: [],
  };
};

const removeBlock = () => {
  block.value = null;
};

const selectBoard = event => {
  const boardId = Number(event.target.value) || null;
  update({ board_id: boardId, board_step_ids: [] });
};

const toggleStep = stepId => {
  const current = block.value.board_step_ids || [];
  update({
    board_step_ids: current.includes(stepId)
      ? current.filter(id => id !== stepId)
      : [...current, stepId],
  });
};

const selectStatus = event => {
  update({ status: event.target.value || null });
};

const updateAttributeFilters = conditions => {
  update({
    attribute_filters: conditions.filter(condition =>
      isValidCondition(condition, definitions.value)
    ),
  });
};

onMounted(async () => {
  const { data } = await BoardsAPI.get();
  boards.value = data.boards;
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <Button
      v-if="!block"
      :label="t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.ADD')"
      icon="i-lucide-kanban"
      variant="link"
      size="sm"
      class="self-start"
      data-test="add-block"
      @click="addBlock"
    />
    <div
      v-else
      class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak bg-n-alpha-1"
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.TITLE') }}
        </span>
        <Button
          icon="i-lucide-trash"
          variant="ghost"
          color="ruby"
          size="sm"
          :title="t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.REMOVE')"
          data-test="remove-block"
          @click="removeBlock"
        />
      </div>
      <div class="grid gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.BOARD') }}
          <select
            :value="block.board_id ?? ''"
            class="h-9 px-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            data-test="board"
            @change="selectBoard"
          >
            <option value="">
              {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.BOARD_PLACEHOLDER') }}
            </option>
            <option v-for="board in boards" :key="board.id" :value="board.id">
              {{ board.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.STATUS') }}
          <select
            :value="block.status ?? ''"
            class="h-9 px-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            data-test="status"
            @change="selectStatus"
          >
            <option value="">
              {{
                t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.STATUS_OPTIONS.ANY')
              }}
            </option>
            <option v-for="status in STATUSES" :key="status" :value="status">
              {{ t(STATUS_LABELS[status]) }}
            </option>
          </select>
        </label>
      </div>
      <p v-if="!block.board_id" class="text-xs text-n-ruby-11">
        {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.BOARD_REQUIRED') }}
      </p>
      <div v-if="steps.length" class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.STEPS') }}
        </span>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="step in steps"
            :key="step.id"
            type="button"
            class="px-2.5 py-1 text-xs rounded-full border"
            :class="
              (block.board_step_ids || []).includes(step.id)
                ? 'border-n-brand text-n-brand bg-n-alpha-2'
                : 'border-n-weak text-n-slate-11'
            "
            :data-test="`step-${step.id}`"
            @click="toggleStep(step.id)"
          >
            {{ step.name }}
          </button>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <Button
          :label="
            t('CONTACTS_LAYOUT.FILTER.SEGMENT_KANBAN.ATTRIBUTES', {
              count: attributeFilters.length,
            })
          "
          icon="i-lucide-sliders-horizontal"
          variant="link"
          size="sm"
          class="self-start"
          data-test="toggle-attributes"
          @click="showAttributes = !showAttributes"
        />
        <TaskAttributeFiltersPanel
          v-if="showAttributes"
          :model-value="attributeFilters"
          :definitions="definitions"
          @update:model-value="updateAttributeFilters"
          @close="showAttributes = false"
        />
      </div>
    </div>
  </div>
</template>
